/**
 * Resident Service — API-ready CRUD for resident lifecycle management.
 *
 * Provides create / read / update / discharge / transfer with localStorage
 * persistence.  Every mutation is audit-logged via the central auditLogger.
 *
 * To migrate to a real backend, replace the body of each static method with
 * the corresponding fetch() / axios call — the return-type contract
 * ({ success, data?, error?, errors? }) stays the same.
 */

import LocalStorageService from '../storage/LocalStorageService';
import { createAuditEntry } from '../../utils/auditLogger';
import { residents as seedResidents } from '../../data/mockData';

const STORE_KEY = 'residents_crud_v1';
const DISCHARGED_KEY = 'residents_discharged_v1';
const LOG_PREFIX = '[ResidentService]';

function _id() {
  return `RES-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function _audit(action, entityId, oldValue, newValue, userId) {
  createAuditEntry({
    eventType: 'data_modification',
    entityType: 'resident',
    entityId,
    action,
    oldValue,
    newValue,
    userId,
  });
}

function _getAllStored() {
  return LocalStorageService.get(STORE_KEY, null);
}

function _saveAll(list) {
  LocalStorageService.set(STORE_KEY, list);
}

const REQUIRED_FIELDS = ['name', 'room'];

function _validate(data) {
  const missing = REQUIRED_FIELDS.filter((f) => !data[f]);
  if (missing.length) return missing.map((f) => `${f} is required`);
  return [];
}

class ResidentService {
  /**
   * Return the canonical resident list: seed data merged with any localStorage
   * overrides / additions.
   */
  static async getAll() {
    let stored = _getAllStored();
    if (!stored) {
      stored = seedResidents.map((r) => ({ ...r }));
      _saveAll(stored);
    }
    return { success: true, data: stored };
  }

  static async getById(id) {
    const { data } = await ResidentService.getAll();
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    const found = data.find((r) => r.id === id || r.id === numId);
    if (!found) return { success: false, error: 'Resident not found' };
    return { success: true, data: found };
  }

  static async create(residentData, userId = 'system') {
    const errors = _validate(residentData);
    if (errors.length) return { success: false, errors };

    const { data: all } = await ResidentService.getAll();
    const now = new Date().toISOString();
    const newResident = {
      ...residentData,
      id: residentData.id || _id(),
      status: 'active',
      admissionDate: residentData.admissionDate || now.split('T')[0],
      createdAt: now,
      updatedAt: now,
      prescriptions: residentData.prescriptions || [],
      allergies: residentData.allergies || [],
      primaryDiagnosis: residentData.primaryDiagnosis || [],
      dietaryRestrictions: residentData.dietaryRestrictions || [],
    };

    all.push(newResident);
    _saveAll(all);
    _audit('create', newResident.id, null, newResident, userId);
    console.log(LOG_PREFIX, 'Created resident', newResident.id);
    return { success: true, data: newResident };
  }

  static async update(id, fields, userId = 'system') {
    const { data: all } = await ResidentService.getAll();
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    const idx = all.findIndex((r) => r.id === id || r.id === numId);
    if (idx === -1) return { success: false, error: 'Resident not found' };

    const prev = { ...all[idx] };
    all[idx] = {
      ...all[idx],
      ...fields,
      id: all[idx].id,
      updatedAt: new Date().toISOString(),
    };
    _saveAll(all);
    _audit('update', all[idx].id, prev, all[idx], userId);
    console.log(LOG_PREFIX, 'Updated resident', all[idx].id);
    return { success: true, data: all[idx] };
  }

  static async discharge(id, reason, date, userId = 'system') {
    const { data: all } = await ResidentService.getAll();
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    const idx = all.findIndex((r) => r.id === id || r.id === numId);
    if (idx === -1) return { success: false, error: 'Resident not found' };
    if (!reason) return { success: false, error: 'Discharge reason is required' };

    const prev = { ...all[idx] };
    const discharged = {
      ...all[idx],
      status: 'discharged',
      dischargeDate: date || new Date().toISOString().split('T')[0],
      dischargeReason: reason,
      updatedAt: new Date().toISOString(),
    };

    const dischargedList = LocalStorageService.get(DISCHARGED_KEY, []);
    dischargedList.push(discharged);
    LocalStorageService.set(DISCHARGED_KEY, dischargedList);

    all.splice(idx, 1);
    _saveAll(all);
    _audit('delete', discharged.id, prev, discharged, userId);
    console.log(LOG_PREFIX, 'Discharged resident', discharged.id, reason);
    return { success: true, data: discharged };
  }

  static async transfer(id, newRoom, newWard, userId = 'system') {
    const { data: all } = await ResidentService.getAll();
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    const idx = all.findIndex((r) => r.id === id || r.id === numId);
    if (idx === -1) return { success: false, error: 'Resident not found' };

    const prev = { room: all[idx].room, ward: all[idx].ward };
    if (newRoom) all[idx].room = newRoom;
    if (newWard) all[idx].ward = newWard;
    all[idx].updatedAt = new Date().toISOString();
    _saveAll(all);
    _audit('update', all[idx].id, prev, { room: all[idx].room, ward: all[idx].ward }, userId);
    console.log(LOG_PREFIX, 'Transferred resident', all[idx].id, '->', newRoom, newWard);
    return { success: true, data: all[idx] };
  }

  static async getDischarged() {
    return { success: true, data: LocalStorageService.get(DISCHARGED_KEY, []) };
  }
}

export default ResidentService;
